import { useContext, useId, useMemo } from 'react';
import { LineChart, useDrawingArea, useXScale, useYScale } from '@mui/x-charts';
// The pointer-interaction state (hovered axis index) isn't re-exported from the
// package root in v7, hence the deep import.
import { InteractionContext } from '@mui/x-charts/context/InteractionProvider';
import { Skeleton } from '@mui/material';
import { IconTrendingDown, IconTrendingUp, IconMinus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Text, Tooltip } from '@fragments';
import { getIntlLocale } from '@i18n';
import {
    useResolvedCssColor,
    formatMoneyCompact,
    moneySymbol,
    bucketLabel,
    computeTickLabelInterval,
} from '@charts/helpers';

import './styles.scss';

const CHART_HEIGHT = 240;
// Headroom above the plot for the peak chip; the chip floats in this margin.
const CHIP_HEIGHT = 22;
const CHIP_GAP = 10;
const TOP_MARGIN = CHIP_HEIGHT + CHIP_GAP + 8;
// The y-domain is padded so the line breathes: a quarter of the range below
// the lowest point (never past zero) and a little above the peak for the dot.
const DOMAIN_PAD_BELOW = 0.3;
const DOMAIN_PAD_ABOVE = 0.1;

// "R$ 1.351.287" and ",96" as separate strings so the fraction can be set in a
// smaller, quieter Text beside the hero figure. Locale-aware via formatToParts.
function splitMoney(cents, currency) {
    const value = (Number(cents) || 0) / 100;
    const parts = new Intl.NumberFormat(getIntlLocale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).formatToParts(value);

    let integer = '';
    let fraction = '';
    let afterDecimal = false;
    parts.forEach(part => {
        if (part.type === 'decimal') afterDecimal = true;
        if (afterDecimal) fraction += part.value;
        else integer += part.value;
    });

    return { symbol: moneySymbol(currency), integer, fraction };
}

// Percent change against the previous period; null when there is no base to
// compare against (a percentage of zero is meaningless).
function growthPercent(current, previous) {
    const cur = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (prev <= 0) return null;
    return ((cur - prev) / prev) * 100;
}

function formatGrowth(pct) {
    const text = pct.toLocaleString(getIntlLocale(), {
        maximumFractionDigits: 1,
        signDisplay: 'exceptZero',
    });
    return `${text}%`;
}

// The chart's one annotation: a hairline dropped from a bucket to the axis, a
// dot on the point, and a chip above the plot naming the value and bucket. It
// follows the pointer — MUI's interaction state gives the hovered x index — and
// rests on the period's peak when nothing is hovered. Rendered inside the chart
// so it can read the live scales + drawing area; the dot borrows the stroke
// gradient, so it matches the line's color at its x.
function HoverMarker({ labels, values, peakIndex, currency, strokeId }) {
    const { axis } = useContext(InteractionContext);
    const { top, left, width, height } = useDrawingArea();
    const xScale = useXScale();
    const yScale = useYScale();

    const hovered = axis?.x?.index;
    const index = hovered != null && values[hovered] != null ? hovered : peakIndex;
    if (index == null || index < 0) return null;

    const label = `${formatMoneyCompact(values[index], currency)} · ${labels[index]}`;
    const baseX = xScale(labels[index]);
    const cy = yScale(values[index]);
    if (baseX === undefined || cy === undefined) return null;

    const band = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
    const cx = baseX + band / 2;

    const chipW = Math.max(64, (label?.length || 0) * 6.4 + 24);
    const half = chipW / 2;
    // The chip stays inside the plot even when the peak sits at an edge; the
    // hairline and dot stay on the true point.
    const chipCx = Math.min(Math.max(cx, left + half), left + width - half);
    const chipY = top - CHIP_HEIGHT - CHIP_GAP;

    return (
        <g className="insights-hero__peak" style={{ pointerEvents: 'none' }}>
            <line
                className="insights-hero__peak-line"
                x1={cx}
                x2={cx}
                y1={cy}
                y2={top + height}
            />
            <circle
                className="insights-hero__peak-dot"
                cx={cx}
                cy={cy}
                r={4}
                style={{ stroke: `url(#${strokeId})` }}
            />
            <rect
                className="insights-hero__peak-chip"
                x={chipCx - half}
                y={chipY}
                width={chipW}
                height={CHIP_HEIGHT}
                rx={CHIP_HEIGHT / 2}
            />
            <text
                className="insights-hero__peak-text"
                x={chipCx}
                y={chipY + CHIP_HEIGHT / 2}
                textAnchor="middle"
                dominantBaseline="central"
            >
                {label}
            </text>
        </g>
    );
}

// Gradients, masks and the glow filter, defined in plot coordinates (via the
// drawing area) rather than each path's bounding box: the area's vertical fade
// then ends exactly on the axis instead of stopping short at the clip, and the
// stroke's color run always spans the plot width whatever the data.
function ChartDefs({ ids, colorFrom, colorTo }) {
    const { left, top, width, height } = useDrawingArea();

    return (
        <defs>
            {/* Stroke: a horizontal run through two neighbouring chart hues,
                older → newer, landing on the brand accent at the latest bucket. */}
            <linearGradient id={ids.stroke} gradientUnits="userSpaceOnUse" x1={left} y1="0" x2={left + width} y2="0">
                <stop offset="0" stopColor={colorFrom} />
                <stop offset="1" stopColor={colorTo} />
            </linearGradient>

            {/* Luminance masks below: white = visible. Edge fade — the line and
                its wash dissolve at the extreme left / right, so no hard corners. */}
            <linearGradient id={ids.edgeFade} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="white" stopOpacity="0" />
                <stop offset="0.08" stopColor="white" stopOpacity="1" />
                <stop offset="0.92" stopColor="white" stopOpacity="1" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask id={ids.edgeMask} maskUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
                <rect x="0" y="0" width="100%" height="100%" fill={`url(#${ids.edgeFade})`} />
            </mask>

            {/* Area wash — faint at the top of the plot, fully gone at the axis,
                and edge-faded like the line (the rect inside carries the edge
                mask, so one mask does both). Subtle on purpose. */}
            <linearGradient id={ids.areaFade} gradientUnits="userSpaceOnUse" x1="0" y1={top} x2="0" y2={top + height}>
                <stop offset="0" stopColor="white" stopOpacity="0.16" />
                <stop offset="0.5" stopColor="white" stopOpacity="0.05" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask id={ids.areaMask} maskUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill={`url(#${ids.areaFade})`}
                    mask={`url(#${ids.edgeMask})`}
                />
            </mask>

            {/* Glow — a blurred, half-strength copy of the line laid under the
                crisp one. It inherits the stroke gradient, so the halo shifts
                hue along with the line. */}
            <filter id={ids.glow} x="-10%" y="-50%" width="120%" height="200%" colorInterpolationFilters="sRGB">
                <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                <feComponentTransfer in="blur" result="soft">
                    <feFuncA type="linear" slope="0.5" />
                </feComponentTransfer>
                <feMerge>
                    <feMergeNode in="soft" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>
    );
}

function TrendChart({ points, granularityType, currency, seriesLabel }) {
    // The line runs from the palette's second hue into the first (= the brand
    // accent), so the newest buckets carry the accent.
    const colorFrom = useResolvedCssColor('--chart-2');
    const colorTo = useResolvedCssColor('--chart-1');
    const uid = useId().replace(/[:]/g, '');
    const ids = useMemo(() => ({
        stroke: `insights-hero-stroke-${uid}`,
        edgeFade: `insights-hero-edge-fade-${uid}`,
        edgeMask: `insights-hero-edge-mask-${uid}`,
        areaFade: `insights-hero-area-fade-${uid}`,
        areaMask: `insights-hero-area-mask-${uid}`,
        glow: `insights-hero-glow-${uid}`,
    }), [uid]);

    const { values, labels, tickLabelInterval, peakIndex, domain } = useMemo(() => {
        const vals = points.map(p => (Number(p.value) || 0) / 100);
        const hourSpansDays = granularityType === 'hour' && points.length > 24;
        const lbls = points.map(p => bucketLabel(p, granularityType, hourSpansDays));

        const max = Math.max(...vals);
        const min = Math.min(...vals);
        const range = max - min || max || 1;
        const peak = max > 0 ? vals.indexOf(max) : -1;

        return {
            values: vals,
            labels: lbls,
            tickLabelInterval: computeTickLabelInterval(points, granularityType),
            peakIndex: peak,
            domain: {
                min: Math.max(0, min - range * DOMAIN_PAD_BELOW),
                max: max + range * DOMAIN_PAD_ABOVE,
            },
        };
    }, [points, granularityType]);

    return (
        <LineChart
            height={CHART_HEIGHT}
            skipAnimation={false}
            series={[{
                data: values,
                label: seriesLabel,
                color: colorTo,
                curve: 'monotoneX',
                area: true,
                showMark: false,
            }]}
            // The marker below is the hover feedback, so MUI's tooltip is off.
            // The axis highlight stays *enabled* (its line is hidden in `sx`)
            // because MUI only listens to pointer moves while a tooltip or an
            // axis highlight is on — turning both off would freeze the marker.
            tooltip={{ trigger: 'none' }}
            axisHighlight={{ x: 'line' }}
            xAxis={[{
                data: labels,
                scaleType: 'point',
                tickLabelInterval,
                disableLine: true,
                disableTicks: true,
            }]}
            yAxis={[{ min: domain.min, max: domain.max }]}
            leftAxis={null}
            margin={{ top: TOP_MARGIN, bottom: 26, left: 0, right: 0 }}
            slotProps={{ legend: { hidden: true } }}
            sx={{
                '.MuiLineElement-root': {
                    strokeWidth: 2,
                    stroke: `url(#${ids.stroke})`,
                    filter: `url(#${ids.glow})`,
                    mask: `url(#${ids.edgeMask})`,
                },
                '.MuiAreaElement-root': {
                    fill: `url(#${ids.stroke})`,
                    mask: `url(#${ids.areaMask})`,
                },
                // Our marker draws the hairline and the dot.
                '.MuiChartsAxisHighlight-root, .MuiLineHighlightElement-root': {
                    display: 'none',
                },
            }}
        >
            <ChartDefs ids={ids} colorFrom={colorFrom} colorTo={colorTo} />

            {points.length > 1 && (
                <HoverMarker
                    labels={labels}
                    values={values}
                    peakIndex={peakIndex}
                    currency={currency}
                    strokeId={ids.stroke}
                />
            )}
        </LineChart>
    );
}

// The page's lead block, set straight on the canvas — no card. Top to bottom:
// growth against the previous period, the gross revenue figure for the selected
// currency, and its trend over the period as a bare line that fades at both ends.
export default function RevenueHero({ report, previousReport, currency, reportLoading }) {
    const { t } = useTranslation('dashboard');

    const current = report?.currencyReports?.find(r => r.currency === currency) || null;
    const previous = previousReport?.currencyReports?.find(r => r.currency === currency) || null;

    const total = current?.summary?.revenue?.total ?? 0;
    const growth = growthPercent(total, previous?.summary?.revenue?.total);
    const trend = growth == null ? null : growth > 0 ? 'up' : growth < 0 ? 'down' : 'flat';

    const timeSeries = current?.revenue;
    const points = timeSeries?.data || [];
    const granularityType = timeSeries?.granularity?.type || 'day';

    const firstLoad = reportLoading && !report;
    const reloading = reportLoading && !!report;

    const { symbol, integer, fraction } = splitMoney(total, currency);
    const valueLabel = t('insights.hero.valueLabel');
    const seriesLabel = t('insights.metrics.grossRevenue.title');

    return (
        <section className={`insights-hero${reloading ? ' insights-hero--reloading' : ''}`}>
            <div className="insights-hero__lead">
                {/* Growth row keeps its height when there is no base to compare
                    against, so the figure below never jumps between loads. */}
                <div className="insights-hero__growth">
                    {firstLoad ? (
                        <Skeleton variant="rounded" width={180} height={20} className="insights-hero__skeleton" />
                    ) : trend && (
                        <>
                            <span className={`insights-hero__delta insights-hero__delta--${trend}`}>
                                {trend === 'up' && <IconTrendingUp />}
                                {trend === 'down' && <IconTrendingDown />}
                                {trend === 'flat' && <IconMinus />}
                                <Text.caption>{formatGrowth(growth)}</Text.caption>
                            </span>
                            <Text.span contrast={6}>{t('insights.hero.vsPrevious')}</Text.span>
                        </>
                    )}
                </div>

                <div className="insights-hero__value-row">
                    {firstLoad ? (
                        <Skeleton variant="rounded" width={320} height={52} className="insights-hero__skeleton" />
                    ) : (
                        <>
                            <span className="insights-hero__figure">
                                <Text.h1 contrast={10} weight={500} className="insights-hero__value">
                                    {symbol} {integer}
                                </Text.h1>
                                {fraction && (
                                    <Text.h3 contrast={6} weight={500} className="insights-hero__fraction">
                                        {fraction}
                                    </Text.h3>
                                )}
                            </span>
                            <Tooltip content={t('insights.metrics.grossRevenue.description')} placement="bottom">
                                <Text.span contrast={6} className="insights-hero__value-label" tabIndex={0}>
                                    {valueLabel}
                                </Text.span>
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>

            <div className="insights-hero__chart" role="img" aria-label={t('insights.hero.chartAria')}>
                {firstLoad ? (
                    <Skeleton variant="rounded" height={CHART_HEIGHT} className="insights-hero__skeleton" />
                ) : !points.length ? (
                    <div className="insights-hero__empty">
                        <Text.p contrast={5}>{t('charts.empty')}</Text.p>
                    </div>
                ) : (
                    <TrendChart
                        points={points}
                        granularityType={granularityType}
                        currency={currency}
                        seriesLabel={seriesLabel}
                    />
                )}
            </div>
        </section>
    );
}
